import { Component, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../core/api.service';
import { countryCodeFromText, countryDisplayName, ISO_COUNTRY_CODES } from '../../core/countries';
import { I18nService } from '../../core/i18n.service';
import { OfferSubmissionPayload } from '../../core/models';

interface CountryOption {
  code: string;
  name: string;
  label: string;
}

type SubmitStatus = 'idle' | 'invalid' | 'captcha' | 'sent' | 'error' | 'fileError';

@Component({ selector: 'app-post-offer', standalone: true, imports: [FormsModule], templateUrl: './post-offer.html', styleUrl: './post-offer.scss', schemas: [CUSTOM_ELEMENTS_SCHEMA] })
export class PostOffer {
  readonly maxPdfBytes = 5 * 1024 * 1024;
  readonly countryOptions: CountryOption[] = ISO_COUNTRY_CODES
    .map(code => {
      const name = countryDisplayName(code);
      return { code, name, label: `${name} (${code})` };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
  countrySearch = '';
  draft = this.emptyDraft();
  selectedPdf: File | null = null;
  status: SubmitStatus = 'idle';
  error = '';
  isSubmitting = false;
  altchaPayload = '';
  captchaError = false;

  constructor(public i18n: I18nService, private api: ApiService) {}

  get captchaChallengeUrl(): string { return this.api.offerSubmissionCaptchaUrl; }

  filteredCountryOptions(): CountryOption[] {
    const search = this.countrySearch.trim().toLowerCase();
    if (!search) return this.countryOptions;
    return this.countryOptions.filter(country => country.name.toLowerCase().includes(search) || country.code.toLowerCase().includes(search));
  }

  setCountrySearch(value: string): void {
    this.countrySearch = value;
  }

  onCaptchaStateChange(event: Event): void {
    const detail = (event as CustomEvent<{ state?: string; payload?: string }>).detail;
    this.altchaPayload = detail?.state === 'verified' && detail.payload ? detail.payload : '';
    this.captchaError = detail?.state === 'error';
    if (this.altchaPayload && this.status === 'captcha') this.status = 'idle';
  }

  onPdfSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.error = '';
    if (!file) { this.selectedPdf = null; return; }
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      this.status = 'fileError';
      this.error = this.i18n.t('postOffer.pdfInvalid');
      input.value = '';
      return;
    }
    if (file.size > this.maxPdfBytes) {
      this.status = 'fileError';
      this.error = this.i18n.t('postOffer.pdfTooLarge');
      input.value = '';
      return;
    }
    this.selectedPdf = file;
    if (this.status === 'fileError') this.status = 'idle';
  }

  async submit(): Promise<void> {
    if (this.isSubmitting) return;
    if (!this.isValidDraft()) { this.status = 'invalid'; return; }
    if (!this.altchaPayload) { this.status = 'captcha'; return; }
    this.isSubmitting = true;
    this.error = '';
    try {
      const payload: OfferSubmissionPayload = { ...this.normalizedDraft(), altcha: this.altchaPayload };
      if (this.selectedPdf) payload.pdf = { filename: this.selectedPdf.name, contentType: this.selectedPdf.type || 'application/pdf', contentBase64: await this.fileToBase64(this.selectedPdf) };
      await this.api.submitOfferSubmission(payload);
      this.status = 'sent';
      this.draft = this.emptyDraft();
      this.countrySearch = '';
      this.selectedPdf = null;
      this.altchaPayload = '';
    } catch (error) {
      this.status = 'error';
      this.error = error instanceof Error ? error.message : this.i18n.t('postOffer.submitError');
    } finally {
      this.isSubmitting = false;
    }
  }

  private emptyDraft() {
    return { contributorEmail: '', title: '', organization: '', country: '', location: '', deadline: '', publishedAt: '', sourceUrl: '', applyLink: '', researchAreas: '', description: '', requirements: '', benefits: '', contact: '' };
  }

  private normalizedDraft(): Omit<OfferSubmissionPayload, 'altcha'> {
    return {
      contributorEmail: this.draft.contributorEmail.trim(), title: this.draft.title.trim(), organization: this.draft.organization.trim(), country: this.draft.country.trim(), location: this.draft.location.trim(), deadline: this.draft.deadline.trim(), publishedAt: this.draft.publishedAt.trim(), sourceUrl: this.draft.sourceUrl.trim(), applyLink: this.draft.applyLink.trim(), researchAreas: this.toList(this.draft.researchAreas), description: this.draft.description.trim(), requirements: this.toList(this.draft.requirements), benefits: this.toList(this.draft.benefits), contact: this.draft.contact.trim()
    };
  }

  private isValidDraft(): boolean {
    const normalized = this.normalizedDraft();
    return Object.entries(normalized).every(([, value]) => Array.isArray(value) ? value.length > 0 : String(value).trim().length > 0)
      && Boolean(countryCodeFromText(normalized.country));
  }

  private toList(value: string): string[] { return value.split(/[\n,]/).map(item => item.trim()).filter(Boolean); }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error(this.i18n.t('postOffer.fileReadError')));
      reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
      reader.readAsDataURL(file);
    });
  }
}
